import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { StatusSummary } from '../../../api/models/StatusSummary';

export interface UseInventoryStatsProps {
  totalProducts: number;
  totalValue: number;
  totalCostValue?: number;
  writeOffCount?: number;
  statusSummary?: StatusSummary[];
  lastActivityDate: string;
  variant?: 'warehouse' | 'branch';
  availabilityStatusSummary?: { status: string; count: number }[];
}

export const useInventoryStats = ({
  totalProducts,
  totalValue,
  totalCostValue = 0,
  writeOffCount = 0,
  statusSummary = [],
  lastActivityDate,
  variant = 'warehouse',
  availabilityStatusSummary = [],
}: UseInventoryStatsProps) => {
  const theme = useTheme();

  // Calcular productos por estado
  const statusCounts = useMemo(() => {
    const assignedProducts = statusSummary.find((s) => s.status === 'ASSIGNED')?.count || 0;
    const unassignedProducts = statusSummary.find((s) => s.status === 'UNASSIGNED')?.count || 0;
    const inTransitProducts = statusSummary.find((s) => s.status === 'IN_TRANSIT')?.count || 0;

    return {
      assignedProducts,
      unassignedProducts,
      inTransitProducts,
    };
  }, [statusSummary]);

  // Mapear estados de disponibilidad
  const availabilityStatusMap = useMemo(() => {
    return new Map(availabilityStatusSummary.map((s) => [s.status, s.count]));
  }, [availabilityStatusSummary]);

  // Obtener icono para estado de disponibilidad
  const getAvailabilityIcon = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return { icon: 'CheckCircleOutline', color: theme.palette.success.main };
      case 'SOLD':
        return { icon: 'ShoppingBag', color: theme.palette.info.main };
      case 'RESERVED':
        return { icon: 'Lock', color: '#4f46e5' };
      case 'PARTIAL_PAYMENT':
        return { icon: 'AccessTime', color: theme.palette.warning.main };
      default:
        return { icon: 'HelpOutline', color: theme.palette.grey[500] };
    }
  };

  // Obtener nombre del estado de disponibilidad
  const getAvailabilityStatusName = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'Disponibles';
      case 'SOLD':
        return 'Vendidos';
      case 'RESERVED':
        return 'Bloqueadas';
      case 'PARTIAL_PAYMENT':
        return 'Pago Pendiente';
      default:
        return status;
    }
  };

  // Estados de disponibilidad disponibles
  const allAvailabilityStatuses = ['AVAILABLE', 'SOLD', 'RESERVED', 'PARTIAL_PAYMENT'];

  // Calcular estadísticas de margen
  const marginInfo = useMemo(() => {
    const margin = totalValue - totalCostValue;
    const marginPercentage = totalValue > 0 ? (margin / totalValue) * 100 : 0;

    return {
      margin,
      marginPercentage,
      isPositiveMargin: margin >= 0,
    };
  }, [totalValue, totalCostValue]);

  return {
    // Información básica
    totalProducts,
    totalValue,
    totalCostValue,
    writeOffCount,
    lastActivityDate,
    variant,

    // Conteos de estado
    ...statusCounts,

    // Estados de disponibilidad
    allAvailabilityStatuses,
    availabilityStatusMap,

    // Utilidades
    getAvailabilityIcon,
    getAvailabilityStatusName,

    // Información de margen
    ...marginInfo,
  };
};
